// frontend/src/components/chat/ConversationList.jsx
// PHASE 3 UPDATE: Added online status indicators
// PHASE 6: Added group chat support with create group functionality
// List of conversations with search, new conversation, groups, and presence

import React, { useState, useEffect } from "react";
import { useChat } from "../../contexts/ChatContext";
import { OnlineStatusBadge } from "./OnlineStatus";
import CreateGroupModal from "./CreateGroupModal";

const ConversationList = () => {
  const {
    conversations,
    isLoading,
    openConversation,
    fetchChatableUsers,
    chatableUsers,
    startConversation,
    isUserOnline,
    requestPresence,
    isSocketConnected,
  } = useChat();

  const [showNewChat, setShowNewChat] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [viewMode, setViewMode] = useState("all"); // 'all', 'direct', 'groups'

  // Filter conversations by search query and view mode
  const filteredConversations = conversations.filter((conv) => {
    // Filter by view mode
    if (viewMode === "direct" && conv.is_group) return false;
    if (viewMode === "groups" && !conv.is_group) return false;

    // Filter by search query
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();

    if (conv.is_group) {
      const groupName = conv.group_name?.toLowerCase() || "";
      return groupName.includes(query);
    } else {
      const name = conv.other_participant?.name?.toLowerCase() || "";
      const preview = conv.last_message_preview?.toLowerCase() || "";
      return name.includes(query) || preview.includes(query);
    }
  });

  // Sort: groups first, then by last message time
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    // Optionally put groups at top
    // if (a.is_group && !b.is_group) return -1;
    // if (!a.is_group && b.is_group) return 1;

    // Sort by last message time (most recent first)
    const aTime = a.last_message_at || a.updated_at || a.created_at;
    const bTime = b.last_message_at || b.updated_at || b.created_at;
    return new Date(bTime) - new Date(aTime);
  });

  // Request presence for all conversation participants
  useEffect(() => {
    if (!isSocketConnected || conversations.length === 0) return;

    const userIds = conversations
      .filter((conv) => !conv.is_group)
      .map((conv) => conv.other_participant?.user_id)
      .filter(Boolean);

    if (userIds.length > 0) {
      requestPresence(userIds);
    }
  }, [conversations, isSocketConnected, requestPresence]);

  // Search for users when typing in new chat
  useEffect(() => {
    if (!showNewChat) return;

    const searchUsers = async () => {
      setIsSearchingUsers(true);
      await fetchChatableUsers(userSearchQuery);
      setIsSearchingUsers(false);
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [userSearchQuery, showNewChat, fetchChatableUsers]);

  // Format relative time
  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Handle starting a new direct conversation
  const handleStartConversation = async (user) => {
    try {
      const conversation = await startConversation(user.user_id);
      setShowNewChat(false);
      setUserSearchQuery("");
      openConversation({
        ...conversation,
        other_participant: user,
      });
    } catch (err) {
      console.error("Failed to start conversation:", err);
    }
  };

  // Handle group created
  const handleGroupCreated = (group) => {
    setShowCreateGroup(false);
    openConversation(group);
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    const r = role?.toLowerCase();
    if (r === "admin") return "danger";
    if (r === "supervisor" || r === "manager") return "warning";
    if (r === "job_coach") return "info";
    return "secondary";
  };

  // Get group counts
  const groupCount = conversations.filter((c) => c.is_group).length;
  const directCount = conversations.filter((c) => !c.is_group).length;

  if (isLoading && conversations.length === 0) {
    return (
      <div className="chat-loading">
        <div
          className="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2">Loading conversations...</span>
      </div>
    );
  }

  return (
    <div className="conversation-list-container">
      {/* Search and New Chat Header */}
      <div className="conversation-list-header">
        {!showNewChat ? (
          <>
            <div className="chat-search-box">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="chat-search-input"
              />
              {searchQuery && (
                <button
                  className="chat-search-clear"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
            <div className="chat-header-actions">
              <button
                className="btn btn-outline-primary btn-sm chat-new-group-btn"
                onClick={() => setShowCreateGroup(true)}
                title="Create group"
              >
                <i className="bi bi-people-fill"></i>
              </button>
              <button
                className="btn btn-primary btn-sm chat-new-btn"
                onClick={() => setShowNewChat(true)}
                title="New message"
              >
                <i className="bi bi-plus-lg"></i>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="chat-search-box flex-grow-1">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search users..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="chat-search-input"
                autoFocus
              />
            </div>
            <button
              className="btn btn-outline-secondary btn-sm ms-2"
              onClick={() => {
                setShowNewChat(false);
                setUserSearchQuery("");
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* View Mode Tabs (only show when not in new chat mode) */}
      {!showNewChat && (
        <div className="conversation-view-tabs">
          <button
            className={`view-tab ${viewMode === "all" ? "active" : ""}`}
            onClick={() => setViewMode("all")}
          >
            All ({conversations.length})
          </button>
          <button
            className={`view-tab ${viewMode === "direct" ? "active" : ""}`}
            onClick={() => setViewMode("direct")}
          >
            <i className="bi bi-person me-1"></i>
            Direct ({directCount})
          </button>
          <button
            className={`view-tab ${viewMode === "groups" ? "active" : ""}`}
            onClick={() => setViewMode("groups")}
          >
            <i className="bi bi-people me-1"></i>
            Groups ({groupCount})
          </button>
        </div>
      )}

      {/* Connection Status */}
      {!isSocketConnected && (
        <div className="chat-connection-status offline">
          <i className="bi bi-wifi-off me-1"></i>
          <small>Connecting...</small>
        </div>
      )}

      {/* New Chat User List */}
      {showNewChat ? (
        <div className="user-list">
          {isSearchingUsers ? (
            <div className="chat-loading py-3">
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span className="visually-hidden">Searching...</span>
              </div>
              <span className="ms-2">Searching users...</span>
            </div>
          ) : chatableUsers.length === 0 ? (
            <div className="chat-empty py-3 text-center text-muted">
              <i className="bi bi-people d-block fs-3 mb-2"></i>
              {userSearchQuery
                ? "No users found matching your search"
                : "No users available to chat with"}
            </div>
          ) : (
            chatableUsers.map((user) => (
              <div
                key={user.user_id}
                className="user-list-item"
                onClick={() => handleStartConversation(user)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleStartConversation(user);
                  }
                }}
              >
                <div className="avatar-with-status">
                  <div className="chat-avatar">{getInitials(user.name)}</div>
                  <OnlineStatusBadge isOnline={isUserOnline(user.user_id)} />
                </div>
                <div className="user-info">
                  <div className="user-name">{user.name}</div>
                  <div className="user-details">
                    <span
                      className={`badge bg-${getRoleBadgeColor(
                        user.role
                      )} badge-sm`}
                    >
                      {user.role}
                    </span>
                    <span className="user-email text-muted ms-2">
                      {user.email}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Conversation List */
        <div className="conversation-list">
          {sortedConversations.length === 0 ? (
            <div className="chat-empty">
              <i
                className={`bi ${
                  viewMode === "groups" ? "bi-people" : "bi-chat-square-text"
                } d-block fs-1 mb-2`}
              ></i>
              {searchQuery ? (
                <p>No conversations match your search</p>
              ) : viewMode === "groups" ? (
                <>
                  <p>No group conversations yet</p>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowCreateGroup(true)}
                  >
                    <i className="bi bi-people-fill me-1"></i>
                    Create a group
                  </button>
                </>
              ) : viewMode === "direct" ? (
                <>
                  <p>No direct messages yet</p>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowNewChat(true)}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Start a conversation
                  </button>
                </>
              ) : (
                <>
                  <p>No conversations yet</p>
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setShowNewChat(true)}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      New message
                    </button>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setShowCreateGroup(true)}
                    >
                      <i className="bi bi-people-fill me-1"></i>
                      New group
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            sortedConversations.map((conv) => {
              const isGroup = conv.is_group === true;
              const participantId = conv.other_participant?.user_id;
              const isOnline =
                !isGroup && participantId ? isUserOnline(participantId) : false;

              return (
                <div
                  key={conv.conversation_id}
                  className={`conversation-item ${
                    conv.unread_count > 0 ? "unread" : ""
                  } ${isGroup ? "is-group" : ""}`}
                  onClick={() => openConversation(conv)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      openConversation(conv);
                    }
                  }}
                >
                  <div className="avatar-with-status">
                    {isGroup ? (
                      <div className="chat-avatar group-avatar">
                        <i className="bi bi-people-fill"></i>
                      </div>
                    ) : (
                      <>
                        <div className="chat-avatar">
                          {getInitials(conv.other_participant?.name)}
                        </div>
                        <OnlineStatusBadge isOnline={isOnline} />
                      </>
                    )}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <span className="conversation-name">
                        {isGroup
                          ? conv.group_name || "Group Chat"
                          : conv.other_participant?.name || "Unknown User"}
                      </span>
                      <span className="conversation-time">
                        {formatTime(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="conversation-preview">
                      <span className="preview-text">
                        {conv.last_message_preview || "No messages yet"}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="unread-badge">
                          {conv.unread_count > 99 ? "99+" : conv.unread_count}
                        </span>
                      )}
                    </div>
                    {/* Show participant count for groups, consumer for DMs */}
                    {isGroup ? (
                      <div className="conversation-context">
                        <i className="bi bi-people me-1"></i>
                        <small>{conv.participant_count || "?"} members</small>
                      </div>
                    ) : (
                      conv.consumer_name && (
                        <div className="conversation-context">
                          <i className="bi bi-person-badge me-1"></i>
                          <small>{conv.consumer_name}</small>
                        </div>
                      )
                    )}
                  </div>
                  {/* Muted indicator */}
                  {conv.is_muted && (
                    <div className="conversation-muted">
                      <i className="bi bi-bell-slash text-muted"></i>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroup && (
        <CreateGroupModal
          onClose={() => setShowCreateGroup(false)}
          onGroupCreated={handleGroupCreated}
        />
      )}
    </div>
  );
};

export default ConversationList;
